import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Blend, Plus, Trash2, History, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

const GoldCalculator = () => {
  // Purity Mixer states
  const [mixerItems, setMixerItems] = useState([{ id: 1, name: '', weight: '', purity: '' }]);
  const [mixerHistory, setMixerHistory] = useState([]);

  // Load mixer history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('goldMixerHistory');
    if (savedHistory) {
      setMixerHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Helper functions for Purity Mixer
  const round = (num) => Math.round(num * 1000) / 1000;
  const formatTwoDecimals = (num) => parseFloat(num).toFixed(2);

  const calculateConversion = (item) => {
    const w = parseFloat(item.weight);
    const p = parseFloat(item.purity);

    if (isNaN(w) || isNaN(p) || w <= 0 || p <= 0) {
      return null;
    }

    const convertedPurity = 91.75;
    const convertedPurityDecimal = convertedPurity / 100;
    const initialPureGold = w * (p / 100);
    const convertedWeight = initialPureGold / convertedPurityDecimal;
    const totalAlloy = convertedWeight - w;
    const silverAlloy = totalAlloy * 0.07;
    const copperAlloy = totalAlloy - silverAlloy;

    return {
      convertedWeight: round(convertedWeight),
      convertedPurity: convertedPurity,
      totalAlloy: round(totalAlloy),
      silverAlloy: round(silverAlloy),
      copperAlloy: round(copperAlloy),
      date: format(new Date(), 'yyyy-MM-dd'),
    };
  };

  // Purity Mixer handlers
  const handleMixerInputChange = (id, field, value) => {
    setMixerItems(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleAddMixerItem = () => {
    if (mixerItems.length < 10) {
      const newId = Math.max(...mixerItems.map(i => i.id), 0) + 1;
      setMixerItems(prev => [...prev, { id: newId, name: '', weight: '', purity: '' }]);
    } else {
      toast.error('Maximum of 10 items reached');
    }
  };

  const handleRemoveMixerItem = (id) => {
    if (mixerItems.length > 1) {
      setMixerItems(prev => prev.filter(item => item.id !== id));
    } else {
      toast.error('You must have at least one gold item');
    }
  };

  const handleNewMixerCalculation = () => {
    setMixerItems([{ id: 1, name: '', weight: '', purity: '' }]);
    toast.success('Cleared for new calculation');
  };

  const saveMixerToHistory = (validResults) => {
    if (validResults.length === 0) return;

    const newHistory = validResults.map(item => ({
      id: Date.now() + Math.random(),
      name: item.name || 'Untitled',
      inputWeight: formatTwoDecimals(item.weight),
      inputPurity: formatTwoDecimals(item.purity),
      ...item.result,
      timestamp: new Date().toISOString()
    }));

    const updatedHistory = [...newHistory, ...mixerHistory].slice(0, 50); // Keep last 50
    setMixerHistory(updatedHistory);
    localStorage.setItem('goldMixerHistory', JSON.stringify(updatedHistory));
    toast.success('Conversion saved to history');
  };

  // Calculate mixer results
  const mixerResults = mixerItems.map(item => ({
    ...item,
    result: calculateConversion(item)
  }));

  const validMixerResults = mixerResults.filter(r => r.result !== null);

  // Calculate totals
  const mixerTotals = validMixerResults.reduce((acc, item) => ({
    totalInputWeight: acc.totalInputWeight + parseFloat(item.weight),
    totalConvertedWeight: acc.totalConvertedWeight + item.result.convertedWeight,
    totalNetAlloy: acc.totalNetAlloy + item.result.totalAlloy,
  }), { totalInputWeight: 0, totalConvertedWeight: 0, totalNetAlloy: 0 });

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold">Gold Purity Mixer</h1>
        <p className="text-muted-foreground mt-1">Convert gold items to 91.75% purity with alloy breakdown</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Input Section */}
        <div className="lg:col-span-2">
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-primary/20">
                  <Blend className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle>Gold Purity Mixer</CardTitle>
                  <CardDescription>Convert gold items to 91.75% purity</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {mixerItems.map(item => (
                <div key={item.id} className="flex flex-col md:flex-row gap-3 p-4 bg-muted/30 rounded-lg border">
                  <div className="flex-1">
                    <Label htmlFor={`name-${item.id}`} className="text-xs">Item Name</Label>
                    <Input
                      id={`name-${item.id}`}
                      placeholder="e.g., Ring 22K"
                      value={item.name}
                      onChange={(e) => handleMixerInputChange(item.id, 'name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="w-full md:w-32">
                    <Label htmlFor={`weight-${item.id}`} className="text-xs">Weight (g)</Label>
                    <Input
                      id={`weight-${item.id}`}
                      type="number"
                      placeholder="10.5"
                      value={item.weight}
                      onChange={(e) => handleMixerInputChange(item.id, 'weight', e.target.value)}
                      className="mt-1"
                      min="0"
                    />
                  </div>
                  <div className="w-full md:w-32">
                    <Label htmlFor={`purity-${item.id}`} className="text-xs">Purity (%)</Label>
                    <Select
                      value={['99.5', '99.9', '100'].includes(item.purity) ? item.purity : 'custom'}
                      onValueChange={(value) => value !== 'custom' && handleMixerInputChange(item.id, 'purity', value)}
                    >
                      <SelectTrigger id={`purity-${item.id}`} className="mt-1">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="99.5">99.5%</SelectItem>
                        <SelectItem value="99.9">99.9%</SelectItem>
                        <SelectItem value="100">100%</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      placeholder="Custom"
                      value={item.purity}
                      onChange={(e) => handleMixerInputChange(item.id, 'purity', e.target.value)}
                      className="mt-1"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={() => handleRemoveMixerItem(item.id)}
                      disabled={mixerItems.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              <div className="flex flex-wrap gap-3 pt-4 border-t">
                <Button onClick={handleNewMixerCalculation} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  New Calculation
                </Button>
                <Button onClick={handleAddMixerItem} disabled={mixerItems.length >= 10}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
                <Button 
                  onClick={() => saveMixerToHistory(validMixerResults)} 
                  disabled={validMixerResults.length === 0}
                  className="ml-auto"
                >
                  <History className="h-4 w-4 mr-2" />
                  Save to History
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Results Display */}
          <Card className="mt-6 shadow-lg">
            <CardHeader>
              <CardTitle>Conversion Results</CardTitle>
              <CardDescription>Gold converted to 91.75% purity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {mixerResults.map((itemResult, index) => (
                  <div key={index}>
                    {!itemResult.result ? (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="font-semibold text-yellow-800">{itemResult.name || 'Item'}</p>
                        <p className="text-sm text-yellow-700">Enter valid weight and purity</p>
                      </div>
                    ) : (
                      <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                        <h3 className="font-bold text-primary mb-2">{itemResult.name || 'Conversion Result'}</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          Input: <span className="font-semibold">{itemResult.weight}g @ {itemResult.purity}%</span>
                        </p>
                        <div className="bg-accent/20 p-3 rounded-lg mb-2">
                          <p className="text-lg font-bold text-accent-foreground">
                            Converted: {formatTwoDecimals(itemResult.result.convertedWeight)}g
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            @ {itemResult.result.convertedPurity}% purity
                          </p>
                        </div>
                        <div className="text-xs space-y-1">
                          <p className="flex justify-between">
                            <span className="text-muted-foreground">Net Alloy Change:</span>
                            <span className="font-semibold">{formatTwoDecimals(itemResult.result.totalAlloy)}g</span>
                          </p>
                          <p className="flex justify-between">
                            <span className="text-muted-foreground">Copper (Cu):</span>
                            <span className="font-semibold text-destructive">{formatTwoDecimals(itemResult.result.copperAlloy)}g</span>
                          </p>
                          <p className="flex justify-between">
                            <span className="text-muted-foreground">Silver (Ag):</span>
                            <span className="font-semibold text-destructive">{formatTwoDecimals(itemResult.result.silverAlloy)}g</span>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {validMixerResults.length > 0 && (
                <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
                  <h4 className="font-bold text-primary mb-2">Totals</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Input Weight</p>
                      <p className="font-bold">{formatTwoDecimals(mixerTotals.totalInputWeight)}g</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Converted Weight</p>
                      <p className="font-bold">{formatTwoDecimals(mixerTotals.totalConvertedWeight)}g</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Total Alloy</p>
                      <p className="font-bold">{formatTwoDecimals(mixerTotals.totalNetAlloy)}g</p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground mt-4 p-3 bg-muted/30 rounded">
                <strong>Formula:</strong> Converted Weight = (Initial Weight × Initial Purity / 100) / 0.9175
              </p>
            </CardContent>
          </Card>
        </div>

        {/* History Section */}
        <div className="lg:col-span-1">
          <Card className="shadow-lg h-fit sticky top-4">
            <CardHeader>
              <div className="flex items-center gap-2">
                <History className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle>Conversion History</CardTitle>
                  <CardDescription>Recent conversions</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-[600px] overflow-y-auto space-y-2">
                {mixerHistory.length === 0 ? (
                  <div className="text-center p-8 text-muted-foreground">
                    No history yet. Save conversions to see them here.
                  </div>
                ) : (
                  mixerHistory.map(item => (
                    <div key={item.id} className="p-3 bg-muted/30 rounded-lg border hover:bg-muted/50 transition">
                      <p className="font-semibold text-sm flex justify-between">
                        <span>{item.name}</span>
                        <span className="text-xs text-muted-foreground">{format(new Date(item.timestamp), 'MMM dd')}</span>
                      </p>
                      <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                        <p>In: {item.inputWeight}g @ {item.inputPurity}%</p>
                        <p>Out: {formatTwoDecimals(item.convertedWeight)}g @ {item.convertedPurity}%</p>
                        <p className="text-primary font-medium">
                          Alloy: {formatTwoDecimals(item.totalAlloy)}g 
                          ({formatTwoDecimals(item.copperAlloy)}g Cu, {formatTwoDecimals(item.silverAlloy)}g Ag)
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {mixerHistory.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-4"
                  onClick={() => {
                    setMixerHistory([]);
                    localStorage.removeItem('goldMixerHistory');
                    toast.success('History cleared');
                  }}
                >
                  Clear History
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GoldCalculator;
